/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Heading, HFlow, Icon, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { PageContainer } from 'components/layout/PageContainer'
import { useFlags } from 'config/useFlagsContext'
import { MChatRiscoEnum } from 'graphql/types.generated'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import { useMemo } from 'react'
import { calculateAge } from 'util/date/calculateAge'

import { MChatAccessDocLink } from '../components/MChatAccessDocLink'
import { MCHAT_RESULTADOS, MChatFieldModel } from '../model-mchat'
import { MChatEscala } from './MChatEscala'

interface MChatResultadoViewProps {
  values: MChatFieldModel
  isInsideModal?: boolean
}

export function MChatResultadoView(props: MChatResultadoViewProps) {
  const {
    values: { resultado, risco },
    isInsideModal,
  } = props

  const {
    atendimentoProfissional: { iniciadoEm: dataAtendimento },
    cidadao: { dataNascimento },
  } = useAtendimentoContext()

  const { M_CHAT_RF_ENABLED } = useFlags()

  const { years, months } = calculateAge(dataNascimento, dataAtendimento.getTime())
  const idadeEmMeses = 12 * years + months

  const title = 'Risco ' + MCHAT_RESULTADOS[risco].shortTitle
  const isRiscoMedio = risco === MChatRiscoEnum.MEDIO

  const theme = useTheme()
  const styles = useMemo(() => createStyles(theme, risco), [risco, theme])

  return (
    <VFlow>
      <VFlow vSpacing={0.5}>
        <Heading level={isInsideModal ? 2 : 3} style={styles.title}>
          <strong>{title}</strong> | {resultado} {resultado === 1 ? 'ponto' : 'pontos'}
        </Heading>
      </VFlow>
      <MChatEscala pontuacao={resultado} risco={risco} isInsideModal={isInsideModal} />
      <VFlow vSpacing={0.5}>
        {isInsideModal && <Text fontWeight='bold'>Encaminhamento</Text>}
        <PageContainer style={styles.alertContainer}>
          <HFlow style={styles.alertHeader} alignItems='center' hSpacing={0.5}>
            <Icon
              icon={isRiscoMedio && M_CHAT_RF_ENABLED ? 'exclamationTriangleFilled' : 'infoCircleFilled'}
              size={1}
              style={styles.title}
            />
            <Text
              dangerouslySetInnerHTML={{ __html: MCHAT_RESULTADOS[risco].encaminhamento(idadeEmMeses) }}
              style={styles.title}
            />
          </HFlow>
          {isInsideModal && isRiscoMedio && (
            <HFlow>
              <VFlow vSpacing={1} style={styles.consultaSeguimento}>
                <HFlow>
                  <Text fontWeight='bold'>Entrevista de seguimento</Text>
                  {!M_CHAT_RF_ENABLED && <MChatAccessDocLink />}
                </HFlow>
                <Text>
                  {!M_CHAT_RF_ENABLED && 'Acesse o documento original para aplicar a avaliação. '}O teste é considerado
                  positivo (risco para TEA) se a criança falhar em quaisquer 2 itens na Entrevista de Seguimento.
                </Text>
              </VFlow>
            </HFlow>
          )}
        </PageContainer>
      </VFlow>
    </VFlow>
  )
}

const createStyles = (theme: Theme, risco: MChatRiscoEnum) => ({
  title: css`
    font-weight: normal;
    color: ${MCHAT_RESULTADOS[risco].textColor};
  `,
  alertContainer: css`
    padding: 0;
    margin-top: 0;
    outline: 1px solid ${MCHAT_RESULTADOS[risco].lineColor};
  `,
  alertHeader: css`
    padding: 0.5rem;
    background-color: ${MCHAT_RESULTADOS[risco].backgroundColor};
    color: ${MCHAT_RESULTADOS[risco].textColor};
  `,
  consultaSeguimento: css`
    margin: 1rem;
  `,
})
